import {
  SubscriptionDateFilterType,
  SubscriptionStatusEnum,
  TCheckoutOrderReport,
  TCheckoutOrderReportTab,
} from './Order'
import {
  TProductDelivery,
  TProductFrequency,
  TProductTypePayment,
} from './Product'
import { TSaleCustomField } from './Sale'
import { TProductVoucherValue } from './Voucher'
import { TBalanceCurrency } from './Tenant'

export enum TCheckoutValueType {
  Plataform = 1,
  Producer,
  CoProducer,
  Affiliated,
  GlobalAffiliated,
  Installments,
  Indicated,
  TeamLeader,
  Unknown,
}

export enum TValueProductTypeEnum {
  Main = 1,
  Orderbump,
  Upsell,
}

export enum TItemTypeSaleEnum {
  Order = 1,
  Subscription,
}

export enum TCheckoutOrderDetailsError {
  Paid = 1,
  InsufficientBalance = 2,
  SuspiciusTransaction = 3,
  RefusedByBank = 4,
  RefusedByBank2 = 5,
  RefusedByBank3 = 6,
  RefusedByBank4 = 7,
  TransactionDeclinedDueToSecurity = 8,
  IncorrectCardDetails = 9,
  ExpiredCardOrIncorrectDate = 10,
  TransactionDeclined = 11,
  IncorrectCardDetails2 = 12,
  CardBlockedCanceledOrInactive = 13,
  RefusedByBank5 = 14,
  RefusedByBank6 = 15,
  TransactionDeclined2 = 16,
  BankErrorOrCardoperatorSystem = 17,
  RejectedDueToManyAttempts = 18,
  TransactionDeclined3 = 19,
  TransactionDeclined4 = 20,
  TransactionDeclined5 = 21,
  TransactionDeclined6 = 22,
  TransactionDeclined7 = 23,
}

export enum TCardDeclineCategory {
  FalhaProcessamento = 1,
  RecusaBanco = 2,
  SaldoLimiteInsuficiente = 3,
  DadosIncorretos = 4,
  CartaoBloqueado = 5,
  BloqueioRetentativas = 6,
  AntifraudeBanco = 7,
  NaoSuportado = 8,
  ErroTecnico = 9,
  ErroConfiguracaoInterna = 10,
}

export enum TCheckoutOrderEmailStatus {
  Sent = 1,
  Failed = 2,
  Opened = 3,
}

export type TCheckoutOrder = {
  id?: string
  codeId?: string
  name: string
  email: string
  productPriceId?: string
  productName: string
  status: string
  paymentyMethod: string
  value: number
  createdDate: Date
  updatedDate: Date

  monthlyFeesPaid?: number
  commissionValue?: number
  totalAmountPaid?: number
  frequency?: TProductFrequency
  valueType?: TCheckoutValueType
  sellerName?: string
  mainProduct?: boolean
  inAssurance?: boolean

  checkoutModelName?: string | null
  totalOrderbumpsSold?: number | null
  productTransaction?: number | null
  utmCampaign?: string | null
  offerName?: string | null

  valueProductType: TValueProductTypeEnum
  itemTypeSale: TItemTypeSaleEnum
  productType: TProductTypePayment

  phone?: string | null
  phoneCountryCode?: string | null
  countryCode?: string | null
  affiliatedCode?: string | null

  reachedOutForAbandonedOrder?: boolean | null
  orderChargeId?: string | null
  reconciliationId?: string | null
  currency?: TBalanceCurrency
}

export type TSubscription = {
  id?: string
  codeId?: string
  name: string
  email: string
  phone?: string | null

  productName: string
  offerName: string
  status: SubscriptionStatusEnum
  paymentyMethod: number
  value: number
  commissionValue?: number
  sellerName?: string | null

  createdDate: Date

  chargeNumbers?: number
  frequency?: TProductFrequency
  currency?: TBalanceCurrency
}

export type TCheckoutOrderPagination = {
  items: TCheckoutOrder[]
  page: number
  rowsPerPage: number
  totalPages: number
  total: number
  info?: number
  infoUsd?: number
}

export type TSubscriptionPagination = {
  items: TSubscription[]
  page: number
  rowsPerPage: number
  totalPages: number
  total: number
  info?: number
  infoUsd?: number
}

export type TCheckoutOrderRecipient = {
  name: string
  value: number
  valueType: TCheckoutValueType
  valueTypeDescription: string
}

export type TCheckoutOrderSubscriptionsPaidItem = {
  id: string
  chargeDate: Date
  value: number
}

export type TCheckoutOrderSubscriptionsPaid = {
  id: string
  code: string
  items: TCheckoutOrderSubscriptionsPaidItem[]
}

export type TCheckoutOrderError = {
  createdDate: Date
  type: TCheckoutOrderDetailsError
  category?: TCardDeclineCategory | null
  customerMessage?: string | null
  retryAllowed?: boolean
}

export type TCheckoutOrderCustomerAddress = {
  street: string
  number: string
  complement: string
  neighborhood: string
  city: string
  state: string
  zipCode: string
}

export type TCheckoutOrderDetails = {
  tenantId: string
  codeId: string
  ProductDeliveryType?: TProductDelivery
  productDeliveryType?: TProductDelivery
  value: number
  basePrice: number
  rates: number
  installments: number
  installmentValues: number
  customerPhone: string
  customerPhoneCountryCode?: string
  customerCountryCode?: string
  customerDocument: string
  customerAddress: TCheckoutOrderCustomerAddress | null
  recipients?: TCheckoutOrderRecipient[]
  subscriptionsChargesPaid?: TCheckoutOrderSubscriptionsPaid

  src?: string | null
  sck?: string | null
  utm_campaign?: string | null
  utm_source?: string | null
  utm_medium?: string | null
  utm_content?: string | null
  utm_term?: string | null
  track_id?: string | null

  voucherIdentifier?: string | null
  voucherValue?: number | null
  voucherType?: TProductVoucherValue | null

  isSubscriptionPaymentCancel?: boolean
  isBlocked?: boolean
  reasonBlocked?: string | null
  isTelegramPrivateGroup?: boolean

  errors?: TCheckoutOrderError[]

  paymentDate?: Date | null
  orderChargeId?: string | null
  checkoutOrderId?: string | null

  refundedProblem?: string | null
  refundedReason?: string | null
  refundClaimedDate?: Date | null
  customFields?: TSaleCustomField[] | null
  quantity: number

  canceledDate?: Date | null

  expectedReleaseAt: Date | null
  expectedReleaseAmount: number | null
  immediateAvailableAmount: number | null
}

export type TSubscriptionTransactionError = {
  createdDate: Date
  type: TCheckoutOrderDetailsError
  category?: TCardDeclineCategory | null
  customerMessage?: string | null
  retryAllowed?: boolean
}

export type TSubscriptionTransaction = {
  referenceNumber: number
  subscriptionId: string
  serviceId: string
  serviceCode: string
  status: string
  createdDate: Date
  attemptNumbers: number
  lastTransaction: {
    subscriptionPaymentId: string
    orderChargeId: string
    status: number
    chargeData: string | null
    attemptNumber: number
    error?: TSubscriptionTransactionError | null
    paymentDate: Date
  }
  inAssurance: boolean
}

export type TSubscriptionOrderData = {
  id: string
  codeId: string
  name: string
  email: string
  phone: string
  productPriceId: string
  productName: string
  offerName: string
  productType: TProductTypePayment
  status: string
  paymentyMethod: string
  value: number
  createdDate: Date
  updatedDate: Date | null
  monthlyFeesPaid: number
  totalAmountPaid: number
  commissionValue: number
  frequency: TProductFrequency
  valueType: TCheckoutValueType
  sellerName: string
  mainProduct: true
  valueProductType: TValueProductTypeEnum
  inAssurance: false
  utmCampaign: string
  voucherIdentifier: string
  voucherValue: number
  itemTypeSale: TItemTypeSaleEnum
  affiliatedCode: null
  reachedOutForAbandonedOrder: false
}

export type TSubscriptionDetails = {
  id: string
  codeId: string
  name: string
  email: string
  phone: string
  phoneCountryCode?: string | null
  countryCode?: string | null

  productName: string
  offerName: string
  paymentyMethod: number
  value: number
  commisionValue: number
  chargeNumbers: number
  frquency: TProductFrequency
  status: SubscriptionStatusEnum
  currency?: TBalanceCurrency
  createdDate: Date
  closingDate?: Date | null
  nextPaymentDate?: Date | null

  orderData: TSubscriptionOrderData | null
  orderDetails: TCheckoutOrderDetails
  transactions: TSubscriptionTransaction[]
}

export type TCheckoutAccess = {
  id?: string
  productId?: string
  productPriceId?: string
  productName: string
  paymentyMethod: string | null
  productPriceCode: string
  productPriceValue: number
  productType?: TProductTypePayment
  productTypeDescription?: string
  checkoutOrderStatus?: string | null
  checkoutModelId: string
  checkoutModelName: string
  transaction: number
  totalOrderbumpsSold: number | null
  checkoutOnlyUser: number
  orderId?: string | null
  customerName?: string | null
  createdDate: Date
}

export type TCheckoutAccessPagination = {
  items: TCheckoutAccess[]
  page: number
  rowsPerPage: number
  totalPages: number
  total: number
  info?: number
}

export type TCheckoutOrderAnticipation = {
  code: string
  name: string
  email: string
  fee: number
  comission: number
  values: number
  withdraws: number
  withdrawFees: number
  available: number
  waitingFunds: number
  total: number
  ourFee: number
  amountReceive: number
  amountPayment: number
}

export type TCheckoutOrderAnticipationPagination = {
  items: TCheckoutOrderAnticipation[]
  page: number
  rowsPerPage: number
  totalPages: number
  total: number
  info?: number
}

export interface ITrackingFilter {
  src?: string | null
  sck?: string | null
  utm_campaign?: string | null
  utm_source?: string | null
  utm_medium?: string | null
  utm_content?: string | null
  utm_term?: string | null
}

export interface ICheckoutOrdersFilter extends ITrackingFilter {
  startDate: Date | null | undefined
  endDate: Date | null | undefined
  sellerType?: number | null
  products?: string[] | null
  affiliateds?: string[] | null
  payments?: string[] | null
  status?: string[] | null
  lastDays?: number
  currency: TBalanceCurrency
}

export interface ICheckoutOrdersReportProps {
  page: number
  rows: number
  search?: string
  type: TCheckoutOrderReportTab
  report: TCheckoutOrderReport
  filters: ICheckoutOrdersFilter
  isAssurance?: boolean
}

export interface ISubscriptionsFilter {
  dateType: SubscriptionDateFilterType
  startDate: Date | null | undefined
  endDate: Date | null | undefined
  sellerType?: number | null
  products?: string[] | null
  affiliateds?: string[] | null
  payments?: string[] | null
  status?: string[] | null
  currency: TBalanceCurrency
}

export interface ISubscriptionsReportProps {
  page: number
  rows: number
  search?: string
  type: SubscriptionStatusEnum
  filters: ISubscriptionsFilter
}

export type TCheckoutOrderEmail = {
  id: string
  checkoutOrderId: string
  subject: string
  sentDate: Date
  status: TCheckoutOrderEmailStatus
  email: string
}
